/**
 * FormField Component
 * Reusable form field with label, input, validation, and help text
 * Integrates seamlessly with React Hook Form
 */

import { forwardRef, type InputHTMLAttributes } from 'react';
import { cn } from '../../lib/utils';

export interface FormFieldProps extends InputHTMLAttributes<HTMLInputElement> {
  label: string;
  error?: string;
  helpText?: string;
  isLoading?: boolean;
  inputMode?:
    | 'none'
    | 'text'
    | 'tel'
    | 'url'
    | 'email'
    | 'numeric'
    | 'decimal'
    | 'search';
}

export const FormField = forwardRef<HTMLInputElement, FormFieldProps>(
  (
    { label, error, helpText, isLoading = false, className, id, ...props },
    ref
  ) => {
    const inputId = id || `field-${label.toLowerCase().replace(/\s+/g, '-')}`;

    return (
      <div>
        <label
          htmlFor={inputId}
          className="block text-sm font-medium text-gray-700 dark:text-gray-200"
        >
          {label}
        </label>
        <div className="mt-1">
          <input
            ref={ref}
            id={inputId}
            disabled={isLoading || props.disabled}
            className={cn(
              'appearance-none block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 dark:focus:ring-offset-gray-900 focus:ring-sky-500 focus:border-sky-500 dark:focus:border-sky-400 disabled:bg-gray-50 dark:disabled:bg-gray-800 disabled:text-gray-500 dark:disabled:text-gray-400 text-base sm:text-sm touch-manipulation transition-colors duration-200',
              // Text and background colors
              'text-gray-900 dark:text-gray-100 bg-white dark:bg-gray-800',
              // High contrast support
              'high-contrast:border-black high-contrast:focus:border-black high-contrast:focus:ring-black',
              error &&
                'border-red-300 dark:border-red-700 focus:border-red-500 dark:focus:border-red-400 focus:ring-red-500 dark:focus:ring-red-400 high-contrast:border-red-700 high-contrast:focus:border-red-700 high-contrast:focus:ring-red-700',
              className
            )}
            aria-invalid={error ? 'true' : 'false'}
            aria-describedby={
              error
                ? `${inputId}-error`
                : helpText
                  ? `${inputId}-help`
                  : undefined
            }
            {...props}
          />
        </div>
        {error && (
          <p
            id={`${inputId}-error`}
            className="mt-1 text-sm text-red-600 dark:text-red-400"
            role="alert"
          >
            {error}
          </p>
        )}
        {helpText && !error && (
          <p
            id={`${inputId}-help`}
            className="mt-1 text-xs text-gray-500 dark:text-gray-400"
          >
            {helpText}
          </p>
        )}
      </div>
    );
  }
);

FormField.displayName = 'FormField';
